import { motion } from "framer-motion";
import { Zap, Globe, Target, AlertTriangle, TrendingDown, ShieldCheck, Clock } from "lucide-react";
import { MANIFESTO } from "../data/site";
import { Reveal, Eyebrow } from "./Reveal";

const ICONS = [Zap, Globe, Target];
const PROOF = [
  { stat: "24/7/365", label: "Availability" },
  { stat: "Nationwide", label: "Coverage" },
  { stat: "1M+", label: "PMs Logged" },
];

const IMPACT_STATS = [
  {
    icon: AlertTriangle,
    stat: "$760",
    unit: "/ hour",
    label: "Cost of Unplanned Downtime",
    desc: "Average revenue lost per hour when a commercial truck is stranded or out of service (ATRI industry data).",
  },
  {
    icon: TrendingDown,
    stat: "3–5×",
    unit: "",
    label: "Reactive vs. Preventative",
    desc: "Emergency, breakdown-driven repairs cost three to five times more than the equivalent scheduled PM service.",
  },
  {
    icon: ShieldCheck,
    stat: "94%",
    unit: "",
    label: "Preventable Breakdowns",
    desc: "The vast majority of on-road failures can be prevented with a structured preventative maintenance program.",
  },
  {
    icon: Clock,
    stat: "60%+",
    unit: "",
    label: "Downtime Saved On-Site",
    desc: "Mobile service eliminates tow time, shop queues, and equipment transport, assets stay where they earn.",
  },
];

export default function Manifesto() {
  return (
    <section
      id="expertise"
      className="bg-mm-surface py-24 sm:py-32 border-y border-mm-green/15 relative overflow-hidden"
      data-testid="manifesto-section"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(0,177,64,0.55) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,177,64,0.55) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />
      {/* Diagonal accent */}
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-mm-green/5 blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 relative">
        {/* Header, split layout */}
        <div className="grid grid-cols-12 gap-8 items-end mb-16 sm:mb-20">
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <Eyebrow>Industrial Expertise</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-heading font-black uppercase tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
                Two decades <br />
                <span className="text-mm-green">in the field.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
            <p className="text-gray-400 leading-relaxed text-base sm:text-lg max-w-md lg:ml-auto lg:text-right">
              Every principle we operate by was earned the same way, on jobsites,
              in oilfields, and under trucks. Not in conference rooms.
            </p>
          </Reveal>
        </div>

        {/* Principles as editorial rows */}
        <div className="border-t border-mm-green/20">
          {MANIFESTO.map((m, i) => {
            const Icon = ICONS[i];
            const proof = PROOF[i];
            return (
              <motion.div
                key={m.no}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group border-b border-mm-green/20 py-10 sm:py-14 grid grid-cols-12 gap-6 sm:gap-8 items-center hover:bg-mm-base/40 transition-colors duration-500"
              >
                {/* Big number */}
                <div className="col-span-3 sm:col-span-2 lg:col-span-2">
                  <div
                    className="font-heading font-black text-6xl sm:text-8xl lg:text-9xl leading-none text-transparent transition-all duration-500 group-hover:text-mm-green/10"
                    style={{ WebkitTextStroke: "1.5px rgba(0,177,64,0.35)" }}
                  >
                    {m.no}
                  </div>
                </div>

                {/* Icon */}
                <div className="col-span-2 sm:col-span-1 lg:col-span-1 flex items-center justify-center">
                  <div className="p-3 border border-mm-green/30 text-mm-green transition-all duration-300 group-hover:bg-mm-green group-hover:text-black group-hover:rotate-3">
                    {Icon && <Icon size={22} strokeWidth={1.8} />}
                  </div>
                </div>

                {/* Title + copy */}
                <div className="col-span-12 sm:col-span-5 lg:col-span-6">
                  <h3 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none group-hover:text-mm-green transition-colors">
                    {m.title}
                  </h3>
                  <p className="mt-4 text-gray-400 leading-relaxed max-w-xl">{m.desc}</p>
                </div>

                {/* Proof stat */}
                <div className="col-span-12 sm:col-span-4 lg:col-span-3 sm:text-right">
                  <div className="inline-flex sm:flex-col items-baseline sm:items-end gap-3 sm:gap-1 border-l-2 border-mm-green pl-5 sm:border-l-0 sm:border-r-2 sm:border-mm-green sm:pl-0 sm:pr-5">
                    <span className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">
                      {proof.stat}
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-mm-green">
                      {proof.label}
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Footer proof strip */}
        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 font-mono text-[11px] tracking-[0.3em] uppercase text-gray-500">
            <span>20+ Years</span>
            <span className="text-mm-green">·</span>
            <span>150+ Field Technicians</span>
            <span className="text-mm-green">·</span>
            <span>1,000,000+ PMs</span>
          </div>
        </Reveal>

        {/* Why This Matters, impact stats */}
        <div className="mt-24 sm:mt-32">
          <div className="grid grid-cols-12 gap-8 items-end mb-12 sm:mb-16">
            <div className="col-span-12 lg:col-span-7">
              <Reveal>
                <Eyebrow>Why This Matters</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h3 className="mt-5 font-heading font-black uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                  The cost of skipping <br />
                  <span className="text-mm-green">professional maintenance.</span>
                </h3>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="col-span-12 lg:col-span-5">
              <p className="text-gray-400 leading-relaxed max-w-md lg:ml-auto lg:text-right">
                The numbers don&apos;t lie. Fleets that invest in structured, on-site
                maintenance dramatically outperform fleets that don&apos;t, in uptime,
                cost per mile, and asset longevity.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-mm-green/15 border border-mm-green/15">
            {IMPACT_STATS.map((s, i) => {
              const SIcon = s.icon;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                  className="group bg-mm-base p-8 sm:p-10 hover:bg-mm-panel transition-colors duration-500"
                  data-testid={`impact-stat-${i}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="p-3 border border-mm-green/30 text-mm-green group-hover:bg-mm-green group-hover:text-black transition-colors duration-300">
                      <SIcon size={22} strokeWidth={1.7} />
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gray-600">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-heading font-black text-white text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight">
                      {s.stat}
                    </span>
                    {s.unit && (
                      <span className="font-mono text-sm uppercase tracking-widest text-mm-green">
                        {s.unit}
                      </span>
                    )}
                  </div>
                  <div className="mt-4 font-mono text-[11px] tracking-[0.25em] uppercase text-mm-green">
                    {s.label}
                  </div>
                  <p className="mt-4 text-gray-400 leading-relaxed max-w-md">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-gray-600 text-center">
            Industry data · Mobile Maintenance field records
          </div>
        </div>
      </div>
    </section>
  );
}
